import { useEffect, useState } from 'react'

import { Attribute } from '@/entities/attributes'
import { linkConstraint } from '@/entities/constraints'
import { Button } from '@/shared/components'
import { Checkbox } from '@/shared/components/Checkbox/Checkbox'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import {
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, List, ListItem, ListItemButton, ListItemText, styled, Typography,
} from '@mui/material'

export type LinkConstraintsDialogProps = {
  open: boolean
  onClose: (refresh?: boolean) => void
  orderAttributes: Attribute[]
  vehicleAttributes: Attribute[]
}

export const LinkConstraintsDialog: React.FC<LinkConstraintsDialogProps> = ({
  open,
  onClose,
  orderAttributes,
  vehicleAttributes,
}) => {
  const [selectedOrderAttribute, setSelectedOrderAttribute] = useState<Attribute | null>(null)
  const [selectedVehicleAttribute, setSelectedVehicleAttribute] = useState<Attribute | null>(null)
  const [isCompatible, setIsCompatible] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useAppDispatch()

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedOrderAttribute(null)
      setSelectedVehicleAttribute(null)
      setIsCompatible(true)
    }
  }, [open])

  // Format attributes for display
  const formatOrderAttributes = orderAttributes.map(attr => ({
    ...attr,
    displayVal: `${attr.description || ''} [${attr.name}]`,
  }))

  const formatVehicleAttributes = vehicleAttributes.map(attr => ({
    ...attr,
    displayVal: `[${attr.name}] ${attr.description || ''}`,
  }))

  const handleCreate = async () => {
    if (!selectedOrderAttribute || !selectedVehicleAttribute) return

    setIsSubmitting(true)

    try {
      await dispatch(linkConstraint({
        orderAttributeId: selectedOrderAttribute.id as number,
        vehicleAttributeId: selectedVehicleAttribute.id as number,
        isCompatible,
      }))

      onClose(true) // Close with refresh flag
    } catch (error) {
      console.error('Error linking constraint:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle>Link Attributes to Create Constraints</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Order Attributes
            </Typography>
            <StyledListContainer>
              <List disablePadding>
                {formatOrderAttributes.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No available order attributes" />
                  </ListItem>
                ) : (
                  formatOrderAttributes.map((attr) => (
                    <ListItemButton
                      key={attr.id}
                      disableRipple
                      selected={selectedOrderAttribute?.id === attr.id}
                      onClick={() => setSelectedOrderAttribute(attr)}
                      divider
                    >
                      <ListItemText primary={attr.displayVal} />
                    </ListItemButton>
                  ))
                )}
              </List>
            </StyledListContainer>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Run Attributes
            </Typography>
            <StyledListContainer>
              <List disablePadding>
                {formatVehicleAttributes.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No available run attributes" />
                  </ListItem>
                ) : (
                  formatVehicleAttributes.map((attr) => (
                    <ListItemButton
                      key={attr.id}
                      disableRipple
                      selected={selectedVehicleAttribute?.id === attr.id}
                      onClick={() => setSelectedVehicleAttribute(attr)}
                      divider
                    >
                      <ListItemText primary={attr.displayVal} />
                    </ListItemButton>
                  ))
                )}
              </List>
            </StyledListContainer>
          </Grid>

          <Grid item xs={12} sx={{ marginTop: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompatible}
                  onChange={(e) => setIsCompatible(e.target.checked)}
                />
              }
              label={<Typography variant="body1" fontWeight="bold">Compatible?</Typography>}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          outlined
          onClick={() => onClose()}
        >
          Close
        </Button>
        <Button
          primary
          onClick={handleCreate}
          disabled={!selectedOrderAttribute || !selectedVehicleAttribute || isSubmitting}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const StyledListContainer = styled('div')`
  height: 250px;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: ${pxToRem(8)};
  
  .MuiListItemButton-root {
    &.Mui-selected {
      background-color: rgba(25, 118, 210, 0.12);
    }
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }
`